import React, { useEffect } from 'react';

import { ROUTES } from '@elara/client-core/constants';

import {
  useAuthFormState,
  useInviteState,
  useNavigate,
  useOnboardingState,
  useRouterState,
  useSessionState,
} from '#states/hooks.js';
import { Alert, AlertDescription, Button, Loader } from '#ui/index.js';
import { AppLayout, AppLayoutTitle } from '#views/layout/AppLayout.jsx';

import { ConversationalOnboarding } from './ConversationalOnboarding.js';

export function OnboardingPage() {
  const onboardingState = useOnboardingState();
  const routerState = useRouterState();
  useEffect(() => {
    if (routerState.currentRoute?.query?.['skip']) {
      void (async () => {
        await onboardingState.completeOnboarding();
        routerState.navigate(ROUTES.HOME, { replace: true });
      })();
    }
  }, []);

  if (onboardingState.needsProfileCreation) {
    return (
      <AppLayout
        title={<AppLayoutTitle title="Get Started" />}
        transitionKey={routerState.currentRoute?.path}
        hideSidebar
      >
        <ProfileCreationStep />
      </AppLayout>
    );
  }

  if (!onboardingState.isReady) {
    return (
      <AppLayout
        title={<AppLayoutTitle title="Get Started" />}
        transitionKey={routerState.currentRoute?.path}
        hideSidebar
      >
        <div className="flex items-center justify-center h-full">
          <Loader loading={true} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={<AppLayoutTitle title="Get Started" />}
      transitionKey={routerState.currentRoute?.path}
      hideSidebar
    >
      <ConversationalOnboarding />
    </AppLayout>
  );
}

function ProfileCreationStep() {
  const onboardingState = useOnboardingState();
  const sessionState = useSessionState();
  const authFormState = useAuthFormState();
  const inviteState = useInviteState();
  const { navigate } = useNavigate();
  const [attempted, setAttempted] = React.useState(false);

  const creationError = onboardingState.profileCreationError;
  const rejection = onboardingState.profileCreationRejection;

  // If no invite code, log user out and redirect to login with error. A server rejection discards
  // the stored code too, so that case is excluded here: the member should see the specific reason
  // below, not the generic "no account" message.
  React.useEffect(() => {
    if (!inviteState.hasValidInviteSession && !creationError) {
      void (async () => {
        authFormState.error = 'We couldn\u2019t find an account for you. You\u2019ll need an invite to join Elara.';
        await sessionState.logout();
        navigate(ROUTES.LOGIN, { replace: true });
      })();
    }
  }, [inviteState.hasValidInviteSession, creationError, authFormState, sessionState, navigate]);

  // Auto-trigger profile creation on mount
  React.useEffect(() => {
    if (attempted || !inviteState.hasValidInviteSession) return;
    setAttempted(true);
    void onboardingState.createProfile();
  }, [attempted, inviteState.hasValidInviteSession, onboardingState]);

  const signOutToSignup = () => {
    void (async () => {
      await sessionState.logout();
      navigate(ROUTES.SIGNUP, { replace: true });
    })();
  };

  if (creationError) {
    // A rejected invite cannot succeed by retrying the same request, so the only useful action is to
    // go back to the signup page (the rejected code has already been discarded) and enter another.
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 max-w-md mx-auto px-4">
        <Alert variant="destructive">
          <AlertDescription>{creationError}</AlertDescription>
        </Alert>
        <div className="flex gap-2">
          {rejection ? (
            <Button onClick={signOutToSignup}>Use a different invite code</Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  setAttempted(false);
                  onboardingState.profileCreationError = undefined;
                }}
              >
                Try again
              </Button>
              <Button variant="outline" onClick={signOutToSignup}>
                Sign out
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!inviteState.hasValidInviteSession) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader loading={true} />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full gap-3">
      <Loader loading={true} />
      <p className="text-muted-foreground">Setting up your account…</p>
    </div>
  );
}
